import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'src/registry/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import {
    SectionBlock,
    SectionBlockHeader,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalErrorMessages } from 'src/utils/messages';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';

import {
    BillingSimulationFormSchemaValues,
    billingSimulationFormSchema,
} from 'src/features/billingSimulation/utils/formSchema';
import { billingSimulationMessages } from 'src/features/billingSimulation/utils/messages';
import { BillingSimulationResult } from 'src/features/billingSimulation/utils/types';
import { useSimulateBilling } from 'src/features/billingSimulation/api/useSimulateBilling';
import BillingSimulationForm from 'src/features/billingSimulation/components/BillingSimulationForm';
import BillingSimulationChargesTable from 'src/features/billingSimulation/components/tables/BillingSimulationChargesTable';
import BillingSimulationFinancialDocumentsTable from 'src/features/billingSimulation/components/tables/BillingSimulationFinancialDocumentsTable';
import BillingSimulationQuantityEventsTable from 'src/features/billingSimulation/components/tables/BillingSimulationQuantityEventsTable';
import BillingSimulationUsageRecordsTable from 'src/features/billingSimulation/components/tables/BillingSimulationUsageRecordsTable';

const toApiDate = (iso: string): string => iso.slice(0, 10);

const BillingSimulationPage = () => {
    const { formatMessage } = useIntl();
    const { servicePlanId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(billingSimulationMessages.billingSimulation));
        return removeTitle;
    }, [formatMessage, setTitle, removeTitle]);

    const [result, setResult] = React.useState<BillingSimulationResult | null>(null);
    const [submittedRange, setSubmittedRange] = React.useState<{ start: string; end: string } | null>(null);

    const form = useForm<BillingSimulationFormSchemaValues>({
        resolver: zodResolver(billingSimulationFormSchema),
        defaultValues: {
            start_date: '',
            end_date: '',
            discount_policy_ids: [],
            voucher_type_ids: [],
        },
    });

    const { mutate, isPending } = useSimulateBilling({
        onSuccess: (data) => {
            setResult(data);
        },
        onError: () => {
            toast({
                title: formatMessage(generalErrorMessages.error),
                description: formatMessage(generalErrorMessages.somethingWentWrongRetry),
                variant: 'destructive',
            });
        },
    });

    const onSubmit = React.useCallback(
        (values: BillingSimulationFormSchemaValues) => {
            const start = toApiDate(values.start_date);
            const end = toApiDate(values.end_date);
            setSubmittedRange({ start, end });
            mutate({
                planId: servicePlanId,
                start_date: start,
                end_date: end,
                discount_policy_ids: values.discount_policy_ids,
                voucher_type_ids: values.voucher_type_ids,
            });
        },
        [mutate, servicePlanId],
    );

    const showResults = isPending || result !== null;

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link">
                <Link to={`/service-plans/${servicePlanId}`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(billingSimulationMessages.backToServicePlan)}
                </Link>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-6">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl">
                            {formatMessage(billingSimulationMessages.billingSimulation)}
                        </SectionBlockTitle>
                    </SectionBlockHeader>
                    <p className="px-4 pb-4 text-sm text-gray-500">
                        {formatMessage(billingSimulationMessages.pageSubtitle)}
                    </p>
                    <div className="px-4 pb-4">
                        <BillingSimulationForm form={form} onSubmit={onSubmit} isPending={isPending} />
                    </div>
                </SectionBlock>

                {showResults && (
                    <SectionBlock>
                        <SectionBlockHeader>
                            <SectionBlockTitle className="text-xl">
                                {formatMessage(billingSimulationMessages.resultsTitle)}
                            </SectionBlockTitle>
                        </SectionBlockHeader>
                        {submittedRange && (
                            <p className="px-4 pb-2 text-sm text-gray-500">
                                <FormattedMessage
                                    {...billingSimulationMessages.resultsSubtitle}
                                    values={{
                                        startDate:
                                            getFormattedDateTime(submittedRange.start)?.formattedDate ??
                                            submittedRange.start,
                                        endDate:
                                            getFormattedDateTime(submittedRange.end)?.formattedDate ??
                                            submittedRange.end,
                                    }}
                                />
                            </p>
                        )}
                        <div className="px-4 pb-4">
                            <Tabs defaultValue="charges">
                                <TabsList>
                                    <TabsTrigger value="charges">
                                        <FormattedMessage
                                            {...billingSimulationMessages.chargesTab}
                                            values={{ count: result?.charges.length ?? 0 }}
                                        />
                                    </TabsTrigger>
                                    <TabsTrigger value="financial-documents">
                                        <FormattedMessage
                                            {...billingSimulationMessages.financialDocumentsTab}
                                            values={{ count: result?.financial_documents.length ?? 0 }}
                                        />
                                    </TabsTrigger>
                                    <TabsTrigger value="quantity-events">
                                        <FormattedMessage
                                            {...billingSimulationMessages.quantityEventsTab}
                                            values={{ count: result?.quantity_events?.length ?? 0 }}
                                        />
                                    </TabsTrigger>
                                    <TabsTrigger value="usage-records">
                                        <FormattedMessage
                                            {...billingSimulationMessages.usageRecordsTab}
                                            values={{ count: result?.usage_records?.length ?? 0 }}
                                        />
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="charges" className="mt-4">
                                    <BillingSimulationChargesTable
                                        charges={result?.charges ?? []}
                                        isLoading={isPending}
                                    />
                                </TabsContent>
                                <TabsContent value="financial-documents" className="mt-4">
                                    <BillingSimulationFinancialDocumentsTable
                                        documents={result?.financial_documents ?? []}
                                        isLoading={isPending}
                                    />
                                </TabsContent>
                                <TabsContent value="quantity-events" className="mt-4">
                                    <BillingSimulationQuantityEventsTable
                                        events={result?.quantity_events ?? []}
                                        isLoading={isPending}
                                    />
                                </TabsContent>
                                <TabsContent value="usage-records" className="mt-4">
                                    <BillingSimulationUsageRecordsTable
                                        records={result?.usage_records ?? []}
                                        isLoading={isPending}
                                    />
                                </TabsContent>
                            </Tabs>
                        </div>
                    </SectionBlock>
                )}
            </div>
        </>
    );
};

export default BillingSimulationPage;
